import { useState } from "react";
import {
  Box,
  ChevronDown,
  ChevronRight,
  Container as ContainerIcon,
  Database,
  FileText,
  Globe,
  KeyRound,
  Layers,
  Lock,
  Network,
  Server,
  Settings,
} from "lucide-react";
import type { ResourceNode } from "@/api/client";
import { Badge, cn } from "@fox1ck-org/ui";

interface ResourceTreeProps {
  nodes: ResourceNode[];
}

const KIND_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Deployment: Layers,
  StatefulSet: Database,
  DaemonSet: Server,
  ReplicaSet: Layers,
  Pod: Box,
  Container: ContainerIcon,
  Service: Network,
  Ingress: Globe,
  ConfigMap: Settings,
  Secret: KeyRound,
  PersistentVolumeClaim: Database,
  ServiceAccount: Lock,
  Job: FileText,
  CronJob: FileText,
};

const KIND_COLORS: Record<string, string> = {
  Deployment: "text-blue-400",
  StatefulSet: "text-purple-400",
  DaemonSet: "text-purple-400",
  ReplicaSet: "text-cyan-400",
  Pod: "text-emerald-400",
  Service: "text-yellow-400",
  Ingress: "text-orange-400",
  ConfigMap: "text-zinc-400",
  Secret: "text-rose-400",
  PersistentVolumeClaim: "text-pink-400",
  ServiceAccount: "text-zinc-400",
};

const HEALTH_DOT: Record<string, string> = {
  Healthy: "bg-emerald-400",
  Progressing: "bg-blue-400 animate-pulse",
  Degraded: "bg-red-400",
  Suspended: "bg-zinc-400",
  Missing: "bg-yellow-400",
  Unknown: "bg-zinc-500",
};

export function ResourceTree({ nodes }: ResourceTreeProps) {
  if (!nodes || nodes.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">Ресурсов нет</p>;
  }

  const childIndex = new Map<string, ResourceNode[]>();
  const allUids = new Set<string>();
  for (const node of nodes) allUids.add(node.uid);
  for (const node of nodes) {
    const refs = node.parentRefs ?? [];
    for (const ref of refs) {
      const list = childIndex.get(ref.uid) ?? [];
      list.push(node);
      childIndex.set(ref.uid, list);
    }
  }

  // Roots = nodes whose parent UID is not in this resource set.
  const roots = nodes.filter((n) => {
    const refs = n.parentRefs ?? [];
    if (refs.length === 0) return true;
    return !refs.some((r) => allUids.has(r.uid));
  });

  return (
    <div className="space-y-0.5">
      {roots.map((node) => (
        <TreeNode
          key={node.uid}
          node={node}
          childIndex={childIndex}
          depth={0}
          defaultOpen={depthIsOpen(0)}
        />
      ))}
    </div>
  );
}

function depthIsOpen(depth: number) {
  return depth < 1;
}

function TreeNode({
  node,
  childIndex,
  depth,
  defaultOpen,
}: {
  node: ResourceNode;
  childIndex: Map<string, ResourceNode[]>;
  depth: number;
  defaultOpen: boolean;
}) {
  const children = childIndex.get(node.uid) ?? [];
  const [open, setOpen] = useState(defaultOpen);
  const Icon = KIND_ICONS[node.kind] ?? Box;
  const color = KIND_COLORS[node.kind] ?? "text-muted-foreground";
  const dot = node.health ? HEALTH_DOT[node.health.status] ?? "bg-zinc-500" : null;
  const hasChildren = children.length > 0;

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-2 py-1 pr-2 rounded-md transition-colors",
          hasChildren && "cursor-pointer hover:bg-accent/40"
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={hasChildren ? () => setOpen((o) => !o) : undefined}
      >
        <button
          type="button"
          className={cn("h-3 w-3 shrink-0 text-muted-foreground", !hasChildren && "opacity-0")}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          aria-label={open ? "Свернуть" : "Развернуть"}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>

        {dot && <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", dot)} />}

        <Icon className={cn("h-3.5 w-3.5 shrink-0", color)} />

        <span className={cn("text-[11px] font-mono uppercase tracking-wide", color)}>
          {node.kind}
        </span>
        <span className="text-sm text-foreground truncate">{node.name}</span>

        {node.info?.map((info) => (
          <Badge key={info.name} variant="neutral" className="text-[9px] py-0">
            {info.name}: {info.value}
          </Badge>
        ))}
      </div>

      {hasChildren && open && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.uid}
              node={child}
              childIndex={childIndex}
              depth={depth + 1}
              defaultOpen={depthIsOpen(depth + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
